import * as React from "react";
import { FetchApi } from "../../FetchApi";
import { setDebounce } from "./setDebounce";

interface SelectFilterOption {
  value: string | number;
  label: string;
}

interface SelectFilterProps {
  name: string;
  label: string;
  endpoint: string;
  valueKey?: string;
  labelKey?: string;
  defaultValue?: string | number;
  placeholder?: string;
  fetchBody?: Record<string, any>;
  transformOptions?: (data: any[]) => SelectFilterOption[];
}

export const SelectFilter: React.FC<SelectFilterProps> = ({
  name,
  label,
  endpoint,
  valueKey = "id",
  labelKey = "name",
  defaultValue = "",
  placeholder = "الكل",
  fetchBody = {},
  transformOptions,
}) => {
  const [options, setOptions] = React.useState<SelectFilterOption[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [selectedValue, setSelectedValue] = React.useState<string | number>(defaultValue || "");
  const [selectedLabel, setSelectedLabel] = React.useState<string>("");
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const hiddenInputRef = React.useRef<HTMLSelectElement>(null);
  const fetchBodyRef = React.useRef(fetchBody);
  const fetchOptionsRef = React.useRef<((searchValue: string) => Promise<void>) | null>(null);

  // Update fetchBody ref when it changes
  React.useEffect(() => {
    fetchBodyRef.current = fetchBody;
  }, [fetchBody]);

  const fetchOptions = React.useCallback(async (searchValue: string = "") => {
    try {
      setLoading(true);
      const requestBody: any = { sorts: "-created_at", ...fetchBodyRef.current };
      
      // Add search parameter if search value exists
      if (searchValue.trim()) {
        requestBody.q = searchValue.trim();
      }

      const response = await FetchApi.get<any>({
        path: endpoint,
        body: requestBody,
      });

      let optionsList: SelectFilterOption[];
      
      if (transformOptions) {
        optionsList = transformOptions(response.data || response);
      } else {
        const data = response.data || response;
        optionsList = data.map((item: any) => ({
          value: item[valueKey],
          label: item[labelKey],
        }));
      }

      setOptions(optionsList);
    } catch (error) {
      console.error(`Error fetching ${endpoint}:`, error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [endpoint, valueKey, labelKey, transformOptions]);

  // Store fetchOptions in ref to avoid dependency issues
  React.useEffect(() => {
    fetchOptionsRef.current = fetchOptions;
    // Initial fetch when fetchOptions is set
    fetchOptions("");
  }, [fetchOptions]);

  // Debounced search - use ref to avoid infinite loop
  React.useEffect(() => {
    if (isOpen && fetchOptionsRef.current) {
      setDebounce(
        () => {
          if (fetchOptionsRef.current) {
            fetchOptionsRef.current(searchQuery);
          }
        },
        `select-filter-${name}-search`,
        500
      );
    }
  }, [searchQuery, isOpen, name]);

  // Handle click outside to close dropdown
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery("");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      // Focus search input when dropdown opens
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Update selected label when options change and value is set
  React.useEffect(() => {
    if (selectedValue && options.length > 0) {
      const selected = options.find(opt => String(opt.value) === String(selectedValue));
      if (selected) {
        setSelectedLabel(selected.label);
      } else {
        // If selected value not found in current options, try to fetch it
        // This handles the case where defaultValue is set but options haven't loaded yet
        if (selectedValue && !loading) {
          setSelectedLabel("");
        }
      }
    } else if (!selectedValue) {
      setSelectedLabel("");
    }
  }, [options, selectedValue, loading]);

  // Handle defaultValue changes (only on mount or when defaultValue prop changes)
  const prevDefaultValueRef = React.useRef(defaultValue);
  React.useEffect(() => {
    if (prevDefaultValueRef.current !== defaultValue) {
      prevDefaultValueRef.current = defaultValue;
      if (defaultValue !== undefined && String(defaultValue) !== String(selectedValue)) {
        setSelectedValue(defaultValue);
        setSelectedLabel("");
      }
    }
  }, [defaultValue, selectedValue]);

  // Helper function to trigger form change
  const triggerFormChange = React.useCallback((value: string) => {
    if (hiddenInputRef.current) {
      const select = hiddenInputRef.current;
      const oldValue = select.value;
      select.value = value;
      
      // Only trigger if value actually changed
      if (oldValue !== value) {
        // Create and dispatch a native change event on the select element
        // This will automatically bubble to the form
        const event = new Event("change", {
          bubbles: true,
          cancelable: true
        });
        
        // Dispatch on the select element - it will bubble to the form
        select.dispatchEvent(event);
      }
    }
  }, []);

  // Update hidden input value and trigger change event for ReactiveForm
  React.useEffect(() => {
    if (hiddenInputRef.current) {
      const newValue = selectedValue ? String(selectedValue) : "";
      if (hiddenInputRef.current.value !== newValue) {
        triggerFormChange(newValue);
      }
    }
  }, [selectedValue, triggerFormChange]);

  const handleSelect = (option: SelectFilterOption) => {
    const newValue = String(option.value);
    setSelectedValue(option.value);
    setSelectedLabel(option.label);
    setIsOpen(false);
    setSearchQuery("");
    
    // Trigger form change immediately
    triggerFormChange(newValue);
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchQuery("");
      // Reset to initial options when opening
      if (fetchOptionsRef.current) {
        fetchOptionsRef.current("");
      }
    }
  };

  const displayText = selectedLabel || placeholder;
  const displayValue = selectedValue ? String(selectedValue) : "";

  return (
    <div className="form-floating" ref={dropdownRef} style={{ position: "relative" }}>
      {/* Hidden select for ReactiveForm compatibility - using actual select element */}
      <select
        ref={hiddenInputRef as React.RefObject<HTMLSelectElement>}
        name={name}
        value={displayValue}
        id={name}
        style={{
          position: "absolute",
          opacity: 0,
          pointerEvents: "none",
          height: "1px",
          width: "1px",
          padding: 0,
          border: "none",
          margin: 0,
          clip: "rect(0, 0, 0, 0)",
          overflow: "hidden"
        }}
        tabIndex={-1}
        onChange={(e) => {
          // Allow the event to bubble to the form
          // The value is already set via our handleSelect
        }}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={String(option.value)}>
            {option.label}
          </option>
        ))}
      </select>
      
      {/* Custom dropdown button - styled as select */}
      <div
        style={{ 
          cursor: "pointer", 
          userSelect: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0.375rem 0.75rem 0.375rem 2.25rem",
          minHeight: "calc(1.5em + 0.75rem + 2px)",
          backgroundColor: "#fff",
          border: "1px solid #ced4da",
          borderRadius: "0.25rem",
          fontSize: "1rem",
          fontWeight: "400",
          lineHeight: "1.5",
          color: "#212529",
          transition: "border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
          appearance: "none",
          backgroundImage: "none"
        }}
        className="form-select"
        onClick={handleToggle}
        onMouseEnter={(e) => {
          if (!isOpen) {
            e.currentTarget.style.borderColor = "#86b7fe";
          }
        }}
        onMouseLeave={(e) => {
          if (!isOpen) {
            e.currentTarget.style.borderColor = "#ced4da";
          }
        }}
      >
        <span style={{ 
          position: "absolute",
          left: "0.75rem",
          pointerEvents: "none",
          color: "#6c757d",
          display: "flex",
          alignItems: "center"
        }}>
          {isOpen ? (
            <i className="fas fa-chevron-up" style={{ fontSize: "0.8em" }} />
          ) : (
            <i className="fas fa-chevron-down" style={{ fontSize: "0.8em" }} />
          )}
        </span>
        <span style={{ 
          color: displayText === placeholder ? "#6c757d" : "inherit",
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          textAlign: "left"
        }}>
          {loading && !isOpen ? "جاري التحميل..." : displayText}
        </span>
      </div>
      
      <label htmlFor={name}>{label}</label>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          className="dropdown-menu show w-100"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            zIndex: 1000,
            maxHeight: "300px",
            overflowY: "auto",
          }}
        >
          {/* Search input - MUI style */}
          <div className="p-2 border-bottom" style={{ backgroundColor: "#f5f5f5" }}>
            <div style={{ position: "relative" }}>
              <i 
                className="fas fa-search" 
                style={{ 
                  position: "absolute", 
                  right: "12px", 
                  top: "50%", 
                  transform: "translateY(-50%)",
                  color: "#9e9e9e",
                  zIndex: 1
                }} 
              />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="بحث..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: "100%",
                  padding: "10px 40px 10px 12px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "4px",
                  fontSize: "14px",
                  backgroundColor: "#fff",
                  outline: "none",
                  transition: "border-color 0.2s, box-shadow 0.2s",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#1976d2";
                  e.target.style.boxShadow = "0 0 0 2px rgba(25, 118, 210, 0.2)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e0e0e0";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>
          </div>

          {/* Options list */}
          <div style={{ maxHeight: "250px", overflowY: "auto" }}>
            {loading ? (
              <div className="dropdown-item text-center text-muted">
                جاري التحميل...
              </div>
            ) : options.length === 0 ? (
              <div className="dropdown-item text-center text-muted">
                لا توجد نتائج
              </div>
            ) : (
              options.map((option) => (
                <div
                  key={option.value}
                  className={`dropdown-item ${
                    String(option.value) === String(selectedValue) ? "active" : ""
                  }`}
                  style={{ cursor: "pointer" }}
                  onClick={() => handleSelect(option)}
                >
                  {option.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};